import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useUppercaseInput } from '@/hooks/useUppercaseInput';
import { productService } from '@/services/products';
import type { Product } from '@/types/api';
import { resolveApiErrorMessage } from '@/utils/errors';
import { parseCurrency } from '@/utils/formatters';

const productSchema = z.object({
  name: z.string().min(2, 'Informe o nome'),
  sku: z.string().optional(),
  unitPrice: z.coerce.number().positive('Informe o valor'),
  notes: z.string().max(1000, 'Máximo de 1000 caracteres').optional(),
});

type ProductFormValues = z.infer<typeof productSchema>;

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: (product: Product) => void;
  product?: Product;
}

export const ProductModal = ({ isOpen, onClose, onSaved, product }: ProductModalProps) => {
  const { handleUppercaseChange } = useUppercaseInput();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: { name: '', sku: '', unitPrice: 0, notes: '' },
  });

  const generateSkuSuggestion = React.useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 900 + 100);
    return `PRO-${year}${month}${day}-${random}`;
  }, []);

  const mutation = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const payload = {
        ...values,
        name: values.name.trim().toUpperCase(),
        sku: values.sku?.trim() ? values.sku.trim().toUpperCase() : undefined,
        notes: values.notes?.trim()?.toUpperCase() || undefined,
      };
      return product ? productService.update(product.id, payload) : productService.create(payload);
    },
    onSuccess: (savedProduct) => {
      toast.success(product ? 'Produto atualizado' : 'Produto criado');
      reset();
      onSaved?.(savedProduct);
      onClose();
    },
    onError: (error) => toast.error(resolveApiErrorMessage(error, 'Erro ao salvar produto')),
  });

  React.useEffect(() => {
    if (product && isOpen) {
      reset({
        name: product.name,
        sku: product.sku ?? '',
        unitPrice: product.unitPrice,
        notes: product.notes ?? '',
      });
    } else if (!product && isOpen) {
      reset({ name: '', sku: generateSkuSuggestion(), unitPrice: 0, notes: '' });
    }
  }, [product, isOpen, reset, generateSkuSuggestion]);

  const onSubmit = (values: ProductFormValues) => {
    mutation.mutate(values);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={product ? 'Editar produto' : 'Novo produto'}
      description="Cadastre seus produtos para agilizar os pedidos"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="name" className="mb-1 block text-sm font-medium text-slate-600">
            Nome
          </label>
          <Input
            id="name"
            placeholder="Nome do produto"
            error={errors.name?.message}
            {...register('name', {
              onChange: handleUppercaseChange,
            })}
          />
        </div>
        <div>
          <label htmlFor="sku" className="mb-1 block text-sm font-medium text-slate-600">
            SKU
          </label>
          <Input id="sku" placeholder="Código interno" {...register('sku')} />
        </div>
        <div>
          <label htmlFor="unitPrice" className="mb-1 block text-sm font-medium text-slate-600">
            Valor unitário
          </label>
          <Input
            id="unitPrice"
            placeholder="R$ 0,00"
            error={errors.unitPrice?.message}
            {...register('unitPrice', {
              setValueAs: (value) => parseCurrency(value?.toString() ?? '0'),
            })}
          />
        </div>
        <div>
          <label htmlFor="notes" className="mb-1 block text-sm font-medium text-slate-600">
            Observações
          </label>
          <textarea
            id="notes"
            className="h-24 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            placeholder="Detalhes adicionais sobre o produto"
            {...register('notes', {
              onChange: handleUppercaseChange,
            })}
          />
          {errors.notes && <p className="mt-1 text-xs text-danger">{errors.notes.message}</p>}
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvando...' : product ? 'Atualizar' : 'Criar produto'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
